// src/app/modules/Settings/Settings.jsx
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from 'react-bootstrap';
import { User, Building2, CreditCard, Lock, X, Clock, Code2, Eye, EyeOff, Globe } from 'lucide-react';
import * as actions from "../../Auth/_redux/authCrud";
import * as authActions from "../../Auth/_redux/authRedux";
import { toast } from 'react-toastify';
import './settings.scss';
import { BillingTab } from './BillingTab';
import { DeveloperTab } from './DeveloperTab';
import { OrganizationTab } from './OrganizationTab';

function PaymentPendingModal({ onClose }) {
  return (
    <div className="sty-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sty-dialog sty-dialog--sm" style={{ textAlign: 'center', padding: '36px 32px' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Clock size={26} color="#2563EB" />
        </div>
        <h5 style={{ fontSize: 17, fontWeight: 700, color: '#1C1C2E', marginBottom: 8 }}>Payment received</h5>
        <p style={{ fontSize: 13, color: '#7E8299', lineHeight: 1.7, marginBottom: 24 }}>
          Your payment is being verified — this usually takes a few seconds.
          Your subscription will update automatically once confirmed.
        </p>
        <button className="sty-btn sty-btn--primary" style={{ width: '100%' }} onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}

export function Settings() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const VALID_TABS = ['profile', 'organization', 'billing', 'developer'];
  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab    = params.get('tab');
    return VALID_TABS.includes(tab) ? tab : 'profile';
  };
  const getActionFromUrl = () => new URLSearchParams(location.search).get('action');

  const activeTab     = getTabFromUrl();
  const initialAction = getActionFromUrl();
  const setActiveTab  = (tab) => navigate(`/settings?tab=${tab}`, { replace: true });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('status') === 'success') {
      setShowPendingModal(true);
      navigate('/settings?tab=billing', { replace: true });
    }
  }, []); // eslint-disable-line

  const { user } = useSelector((state) => ({ user: state.auth.user }), shallowEqual);

  const [profileData, setProfileData] = useState({
    first_name: '', last_name: '', email: '', phone_number: '',
    language: '', locale: '', timezone: '',
  });
  const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '', confirm_password: '' });

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name:   user.first_name   || '',
        last_name:    user.last_name    || '',
        email:        user.email        || '',
        phone_number: user.phone_number || '',
        language:     user.language     || 'en',
        locale:       user.locale       || 'en-US',
        timezone:     user.timezone     || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      });
    }
  }, [user]);

  async function handleSaveProfile() {
    setLoading(true);
    try {
      const response = await actions.updateUserProfile({
        first_name: profileData.first_name,
        last_name:  profileData.last_name,
        language:   profileData.language  || undefined,
        locale:     profileData.locale    || undefined,
        timezone:   profileData.timezone  || undefined,
      });
      if (response.data?.user) dispatch(authActions.actions.fulfillUser(response.data.user));
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  }

  async function handleChangePassword() {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match'); return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters'); return;
    }
    setLoading(true);
    try {
      await actions.changePassword({ old_password: passwordData.old_password, new_password: passwordData.new_password });
      toast.success('Password changed');
      setShowPasswordModal(false);
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  }

  const TABS = [
    { id: 'profile',      Icon: User,        label: 'Profile' },
    { id: 'organization', Icon: Building2,   label: 'Organization' },
    { id: 'billing',      Icon: CreditCard,  label: 'Billing' },
    { id: 'developer',    Icon: Code2,       label: 'Developer' },
  ];

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <>
      <div className="sty-page">
        {/* ── Page header ─────────────────────────────────────────────── */}
        <div className="sty-header">
          <div>
            <h1 className="sty-header__title">Settings</h1>
            <p className="sty-header__sub">Manage your account and organization preferences</p>
          </div>
        </div>

        {/* ── Tab rail ────────────────────────────────────────────────── */}
        <div className="sty-tabs">
          {TABS.map(({ id, Icon, label }) => (
            <button
              key={id}
              className={`sty-tab ${activeTab === id ? 'sty-tab--active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Profile tab ─────────────────────────────────────────────── */}
        {activeTab === 'profile' && (
          <div className="sty-card">
            {/* Avatar + name header */}
            <div className="sty-profile-hero">
              <div className="sty-avatar">{initials}</div>
              <div>
                <div className="sty-profile-hero__name">
                  {user?.first_name} {user?.last_name}
                </div>
                <div className="sty-profile-hero__email">{user?.email}</div>
              </div>
            </div>

            <div className="sty-card__divider" />

            <div className="sty-card__body">
              <div className="sty-section-label">Personal information</div>
              <div className="sty-field-grid">
                <div className="sty-field">
                  <label>First name</label>
                  <input
                    type="text"
                    className="sty-input"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData(p => ({ ...p, first_name: e.target.value }))}
                  />
                </div>
                <div className="sty-field">
                  <label>Last name</label>
                  <input
                    type="text"
                    className="sty-input"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData(p => ({ ...p, last_name: e.target.value }))}
                  />
                </div>
                <div className="sty-field">
                  <label>Email address</label>
                  <input type="email" className="sty-input sty-input--disabled" value={profileData.email} disabled />
                  <span className="sty-field__hint">Email cannot be changed</span>
                </div>
                <div className="sty-field">
                  <label>Phone number</label>
                  <input type="tel" className="sty-input sty-input--disabled" value={profileData.phone_number} disabled />
                  <span className="sty-field__hint">Contact support to change</span>
                </div>
              </div>
            </div>

            <div className="sty-card__divider" />

            <div className="sty-card__body">
              <div className="sty-section-label"><Globe size={14} style={{ marginRight: 6 }} />Language &amp; Region</div>
              <div className="sty-field-grid">
                <div className="sty-field">
                  <label>Language</label>
                  <select
                    className="sty-input"
                    value={profileData.language}
                    onChange={(e) => setProfileData(p => ({ ...p, language: e.target.value }))}
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="pt">Portuguese</option>
                    <option value="ar">Arabic</option>
                    <option value="es">Spanish</option>
                    <option value="de">German</option>
                    <option value="zh">Chinese</option>
                    <option value="sw">Swahili</option>
                    <option value="ha">Hausa</option>
                    <option value="yo">Yoruba</option>
                    <option value="ig">Igbo</option>
                  </select>
                </div>
                <div className="sty-field">
                  <label>Locale</label>
                  <select
                    className="sty-input"
                    value={profileData.locale}
                    onChange={(e) => setProfileData(p => ({ ...p, locale: e.target.value }))}
                  >
                    <option value="en-US">English (US)</option>
                    <option value="en-GB">English (UK)</option>
                    <option value="en-NG">English (Nigeria)</option>
                    <option value="en-GH">English (Ghana)</option>
                    <option value="en-KE">English (Kenya)</option>
                    <option value="en-ZA">English (South Africa)</option>
                    <option value="fr-FR">French (France)</option>
                    <option value="fr-CI">French (Côte d'Ivoire)</option>
                    <option value="pt-BR">Portuguese (Brazil)</option>
                    <option value="pt-PT">Portuguese (Portugal)</option>
                    <option value="ar-SA">Arabic (Saudi Arabia)</option>
                    <option value="ar-AE">Arabic (UAE)</option>
                    <option value="es-ES">Spanish (Spain)</option>
                    <option value="es-MX">Spanish (Mexico)</option>
                  </select>
                </div>
                <div className="sty-field sty-field--span2">
                  <label>Timezone</label>
                  <select
                    className="sty-input"
                    value={profileData.timezone}
                    onChange={(e) => setProfileData(p => ({ ...p, timezone: e.target.value }))}
                  >
                    {[
                      'UTC',
                      'Africa/Lagos', 'Africa/Abidjan', 'Africa/Accra', 'Africa/Nairobi',
                      'Africa/Johannesburg', 'Africa/Cairo', 'Africa/Casablanca',
                      'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
                      'America/Sao_Paulo', 'America/Toronto', 'America/Mexico_City',
                      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
                      'Asia/Dubai', 'Asia/Riyadh', 'Asia/Kolkata', 'Asia/Singapore',
                      'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney',
                    ].map(tz => (
                      <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <span className="sty-field__hint">Used to display check-in times in your local time</span>
                </div>
              </div>
            </div>

            <div className="sty-card__footer">
              <button className="sty-btn sty-btn--ghost" onClick={() => setShowPasswordModal(true)}>
                <Lock size={14} /> Change password
              </button>
              <button className="sty-btn sty-btn--primary" onClick={handleSaveProfile} disabled={loading}>
                {loading ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}

        {/* ── Organization tab ────────────────────────────────────────── */}
        {activeTab === 'organization' && (
          <div className="sty-card sty-card--flush">
            <OrganizationTab />
          </div>
        )}

        {/* ── Billing tab ─────────────────────────────────────────────── */}
        {activeTab === 'billing' && (
          <div className="sty-card sty-card--flush">
            <BillingTab initialAction={initialAction} />
          </div>
        )}

        {/* ── Developer tab ───────────────────────────────────────────── */}
        {activeTab === 'developer' && (
          <div className="sty-card sty-card--flush">
            <DeveloperTab />
          </div>
        )}
      </div>

      {/* ── Change Password Modal ──────────────────────────────────────── */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
        <div className="sty-modal">
          <div className="sty-modal__header">
            <h5>Change password</h5>
            <button className="sty-modal__close" onClick={() => setShowPasswordModal(false)}><X size={18} /></button>
          </div>
          <div className="sty-modal__body">
            {[
              { key: 'old_password',     label: 'Current password',  show: showOld,     setShow: setShowOld },
              { key: 'new_password',     label: 'New password',      show: showNew,     setShow: setShowNew,     hint: 'At least 8 characters' },
              { key: 'confirm_password', label: 'Confirm password',  show: showConfirm, setShow: setShowConfirm },
            ].map(({ key, label, show, setShow, hint }) => (
              <div className="sty-field" key={key}>
                <label>{label}</label>
                <div className="sty-input-wrap">
                  <input
                    type={show ? 'text' : 'password'}
                    className="sty-input sty-input--icon-right"
                    value={passwordData[key]}
                    onChange={(e) => setPasswordData(p => ({ ...p, [key]: e.target.value }))}
                  />
                  <button className="sty-input-toggle" type="button" onClick={() => setShow(v => !v)}>
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {hint && <span className="sty-field__hint">{hint}</span>}
              </div>
            ))}
          </div>
          <div className="sty-modal__footer">
            <button className="sty-btn sty-btn--ghost" onClick={() => setShowPasswordModal(false)}>Cancel</button>
            <button className="sty-btn sty-btn--primary" onClick={handleChangePassword} disabled={loading}>
              {loading ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </div>
      </Modal>

      {showPendingModal && <PaymentPendingModal onClose={() => setShowPendingModal(false)} />}
    </>
  );
}